'use client';

import React from 'react';
import type { TSocialExperienceLink, TExperienceSocialPlatform } from '@shared/schemas';
import { SOCIAL_PLATFORMS_REGISTRY, PLATFORMS_BY_ID } from './socialPlatforms';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronUp, ChevronDown, Trash2, Plus } from 'lucide-react';

interface Props {
	links: TSocialExperienceLink[];
	onChange: (nextLinks: TSocialExperienceLink[]) => void;
	title?: string;
	description?: string;
}

export function SocialLinksManager({ links, onChange, title, description }: Props) {
	const addPlatform = (platformId: TExperienceSocialPlatform) => {
		const meta = PLATFORMS_BY_ID[platformId] || PLATFORMS_BY_ID.website;
		const next: TSocialExperienceLink = {
			platform: platformId,
			url: '',
			label: meta?.defaultLabel ?? platformId,
		};
		onChange([...links, next]);
	};

	const removeLink = (index: number) => {
		onChange(links.filter((_, idx) => idx !== index));
	};

	const moveLink = (index: number, direction: 'up' | 'down') => {
		const targetIndex = direction === 'up' ? index - 1 : index + 1;
		if (targetIndex < 0 || targetIndex >= links.length) return;
		const copy = [...links];
		const temp = copy[index];
		const target = copy[targetIndex];
		if (!temp || !target) return;
		copy[index] = target;
		copy[targetIndex] = temp;
		onChange(copy);
	};

	const updateLink = (index: number, patch: Partial<TSocialExperienceLink>) => {
		const updated = links.map((item, idx) => {
			if (idx !== index) return item;
			return { ...item, ...patch };
		});
		onChange(updated);
	};

	const handleUrlBlur = (index: number, rawUrl: string, platformId: TExperienceSocialPlatform) => {
		const meta = PLATFORMS_BY_ID[platformId];
		if (!meta || !rawUrl.trim()) return;
		const formatted = meta.formatUrl(rawUrl);
		updateLink(index, { url: formatted });
	};

	return (
		<div className="space-y-4">
			{(title || description) && (
				<div>
					{title && <h3 className="text-sm font-semibold text-slate-900">{title}</h3>}
					{description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
				</div>
			)}

			{/* Active Social Cards List */}
			{links.length > 0 && (
				<div className="space-y-3">
					{links.map((link, idx) => {
						const meta = PLATFORMS_BY_ID[link.platform] || {
							id: link.platform,
							name: link.platform,
							defaultLabel: link.platform,
							urlPlaceholder: 'https://…',
							iconBg: 'bg-slate-800 text-white',
							renderIcon: (cls?: string) => <Plus className={cls} />,
							formatUrl: (raw: string) => raw,
						};

						return (
							<div
								key={`${link.platform}-${idx}`}
								className="rounded-xl border border-slate-200/90 bg-white p-3.5 shadow-xs transition hover:border-slate-300"
							>
								{/* Card Header: Brand Icon + Reorder & Delete Controls */}
								<div className="flex items-center justify-between pb-3 border-b border-slate-100">
									<div className="flex items-center gap-2.5">
										<div
											className={`size-7 rounded-full flex items-center justify-center text-xs font-bold shadow-2xs ${meta.iconBg}`}
										>
											{meta.renderIcon('size-3.5')}
										</div>
										<span className="text-xs font-bold text-slate-900">{meta.name}</span>
									</div>

									<div className="flex items-center gap-1">
										<button
											type="button"
											disabled={idx === 0}
											onClick={() => moveLink(idx, 'up')}
											aria-label="Move up"
											className="p-1 rounded-md border border-slate-200 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:pointer-events-none transition cursor-pointer"
										>
											<ChevronUp className="size-3.5" />
										</button>
										<button
											type="button"
											disabled={idx === links.length - 1}
											onClick={() => moveLink(idx, 'down')}
											aria-label="Move down"
											className="p-1 rounded-md border border-slate-200 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:pointer-events-none transition cursor-pointer"
										>
											<ChevronDown className="size-3.5" />
										</button>
										<button
											type="button"
											onClick={() => removeLink(idx)}
											aria-label="Delete link"
											className="p-1 rounded-md border border-rose-200 hover:bg-rose-50 text-rose-600 transition cursor-pointer ml-1"
										>
											<Trash2 className="size-3.5" />
										</button>
									</div>
								</div>

								{/* Card Body: 2 Input Fields Side-by-Side (URL/Username on left, Button Text on right) */}
								<div className="mt-3 grid gap-3 sm:grid-cols-[1.5fr_1fr]">
									<div className="space-y-1">
										<Label className="text-[11px] font-semibold text-slate-700">
											URL / Username <span className="text-rose-500">*</span>
										</Label>
										<Input
											type="text"
											value={link.url}
											onChange={(e) => updateLink(idx, { url: e.target.value })}
											onBlur={(e) => handleUrlBlur(idx, e.target.value, link.platform)}
											placeholder={meta.urlPlaceholder}
											className="h-9 text-xs bg-slate-50/50"
										/>
									</div>

									<div className="space-y-1">
										<Label className="text-[11px] font-semibold text-slate-700">
											Button Text <span className="text-slate-400 font-normal">(optional)</span>
										</Label>
										<Input
											type="text"
											value={link.label ?? ''}
											onChange={(e) => updateLink(idx, { label: e.target.value })}
											placeholder={`e.g. ${meta.defaultLabel}`}
											className="h-9 text-xs bg-slate-50/50"
										/>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* Palette of Available Social Networks (Icon Tray) */}
			<div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-3.5">
				<p className="text-xs font-semibold text-slate-700 mb-2.5">
					Click an icon below to add a social channel:
				</p>
				<div className="flex flex-wrap gap-2.5">
					{SOCIAL_PLATFORMS_REGISTRY.map((p) => {
						const isAdded = links.some((l) => l.platform === p.id);
						return (
							<button
								key={p.id}
								type="button"
								onClick={() => addPlatform(p.id)}
								title={`Add ${p.name}`}
								className={`group relative size-9 rounded-full flex items-center justify-center transition cursor-pointer transform hover:scale-110 active:scale-95 shadow-xs ${p.iconBg} ${
									isAdded ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-white' : ''
								}`}
							>
								{p.renderIcon('size-4')}
								{isAdded && (
									<span className="absolute -top-1 -right-1 size-3 rounded-full bg-blue-600 border-2 border-white flex items-center justify-center text-[7px] text-white font-bold">
										✓
									</span>
								)}
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
}
